import { useEffect, useRef } from 'react';

/**
 * Drive an Apollo query's polling from page visibility (spec 0008): poll at
 * `intervalMs` while the tab is visible, stop entirely while it's hidden —
 * a backgrounded ERP tab must not keep hammering the badge/rollup queries.
 *
 * Apollo 3.3 has no `skipPollAttempt`, so this drives `startPolling` /
 * `stopPolling` off `visibilitychange` instead. Pass `enabled=false` to keep
 * polling off regardless (e.g. no group selected yet).
 *
 * `refetchOnVisible` (0103 review): Apollo's `startPolling` schedules the
 * FIRST poll a whole interval later, so a tab that comes back after an hour
 * would show hour-old numbers for another minute. When given, it is called
 * once on each hidden → visible transition — never on mount (the query's own
 * first fetch covers that) and never twice for one return. Kept in a ref so
 * an unstable callback identity does not restart polling every render.
 */
export const usePageVisiblePoll = (
  startPolling: (pollInterval: number) => void,
  stopPolling: () => void,
  intervalMs: number,
  enabled = true,
  refetchOnVisible?: () => unknown,
): void => {
  const refetchRef = useRef(refetchOnVisible);
  refetchRef.current = refetchOnVisible;

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return undefined;
    }
    let hidden = document.visibilityState === 'hidden';
    const sync = () => {
      if (document.visibilityState === 'hidden') {
        hidden = true;
        stopPolling();
        return;
      }
      startPolling(intervalMs);
      if (!hidden) return;
      hidden = false;
      const refetch = refetchRef.current;
      // A failed refetch surfaces through the query's own error state; don't
      // leave an unhandled rejection behind.
      if (refetch) Promise.resolve(refetch()).catch(() => undefined);
    };
    if (hidden) stopPolling();
    else startPolling(intervalMs);
    document.addEventListener('visibilitychange', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      stopPolling();
    };
  }, [startPolling, stopPolling, intervalMs, enabled]);
};
